import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { ContentWrapper, ContinueButton, Form, InputBox, ItemWrapper } from "../styles";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { fetchBankList } from "../../redux/slice";
import CustomSelect from "../../../../components/common/CustomSelect";
import { Input } from "../../../../components/common/Input";
import { accountTypes } from "../../utils";
import { Button } from "../../../../components/common/Button";
import { savedPayoutV2Data } from "../../redux/formDataSlice";

type SelectedBankObject = {
    label: string;
    value: string;
    originalCode?: string;
};

type KESBeneficiaryTypes = {
    handleNext?: () => void;
    handlePrevious?: () => void;
    paymentMethod?: string;
    paymentMethodName?: string;
    bankName?: string;
    accountNumber?: string;
    accountName?: string;
    paymentDescription?: string;
    bankCode?: string;
    selectedBank?: SelectedBankObject;
    serviceCode?: string;
    accountType?: string;
    description?: string;
};

const Beneficiary = ({ handleNext, handlePrevious }: KESBeneficiaryTypes) => {
    const {
        handleSubmit,
        control,
        formState: { errors },
        setValue,
    } = useForm<KESBeneficiaryTypes>();

    const dispatch = useAppDispatch();
    const { businessId } = useAppSelector((state) => state.users);
    const { keys } = useAppSelector((state) => state.settings);
    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);

    const [banks, setBanks] = useState<{ code: string, name: string }[]>()
    const [userData, setUserData] = useState<KESBeneficiaryTypes>();

    const publicKey = keys?.data?.publicKey;
    const PAYMENT_METHOD_MOBILE = userData?.selectedBank?.label.includes("MOBILE MONEY")
    const PAYMENT_METHOD_MPESA = userData?.selectedBank?.value.includes("M-PESA")

    useEffect(() => {
        if (getSavedData) {
            Object.keys(getSavedData).forEach((key) => {
                setValue(
                    key as keyof KESBeneficiaryTypes,
                    getSavedData[key as keyof KESBeneficiaryTypes],
                );
                setValue('bankName', getSavedData.bankCode)
            });
        }
    }, [getSavedData, setValue]);

    useEffect(() => {
        const fetchBanks = async () => {
            const action = await dispatch(
                fetchBankList({
                    xAuthToken: publicKey,
                    country: "KES",
                    type: undefined,
                }),
            );

            if (fetchBankList.fulfilled.match(action)) {
                setBanks(action.payload.data);
            }
        };

        fetchBanks().then((r) => r);
    }, [businessId]);

    const selectBanks = useMemo(() => {
        return banks && Array.isArray(banks)
            ? banks
                .filter((bank) => bank.name !== "National Bank" && bank.code !== "AIRTEL")
                .map((bank, index) => ({
                    label: bank.name,
                    value: `${bank.code}_${index}`,
                    originalCode: bank.code,
                }))
            : [];
    }, [banks]);

    const bankLookupMap = useMemo(() => {
        return selectBanks.reduce((acc, bank) => {
            acc[bank.value] = bank;
            return acc;
        }, {});
    }, [selectBanks]);


    const handleDataUpdate = (field: keyof KESBeneficiaryTypes, value: any) => {
        setUserData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleBankChange = (selectedModifiedValue) => {
        const positionOfUnderscore = selectedModifiedValue.indexOf("_");
        const originalBankCode = selectedModifiedValue.substring(0, positionOfUnderscore);

        const selectedBank = bankLookupMap[selectedModifiedValue];

        setValue("bankCode", originalBankCode);
        handleDataUpdate("bankName", selectedBank?.label);
        handleDataUpdate("selectedBank", selectedBank);
        handleDataUpdate("bankCode", originalBankCode)
    };

    const onSubmit: SubmitHandler<KESBeneficiaryTypes> = useCallback(
        (data) => {
            const selectedBank = bankLookupMap[data.bankName]

            const combinedData = {
                ...userData,
                ...data,
                bankName: selectedBank?.label,
                bankCode: data.bankName
            };

            dispatch(savedPayoutV2Data(combinedData));
            handleNext?.();
        },
        [dispatch, userData, bankLookupMap],
    );

    return (
        <ContentWrapper>
            <Form>
                <InputBox>
                    <Controller
                        name="bankName"
                        control={control}
                        rules={{
                            required: "Please input the beneficiary's Bank name",
                        }}
                        render={({ field }) => (
                            <CustomSelect
                                placeholder={"Select bank/Wallet type"}
                                $width="100%"
                                field={field}
                                disableLabel
                                options={selectBanks}
                                onChange={handleBankChange}
                                showSearch
                                filterOption={(inputValue, option) =>
                                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                                }
                                lightBorder={true}
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.bankName?.message}
                    </span>
                </InputBox>

                {
                    !PAYMENT_METHOD_MOBILE &&
                    PAYMENT_METHOD_MPESA && (
                        <div>
                            <InputBox>
                                <Controller
                                    name="serviceCode"
                                    control={control}
                                    rules={{
                                        required: "Please enter the beneficiary's bank short code",
                                    }}
                                    render={({ field }) => (
                                        <Input
                                            type="text"
                                            label="Bank/Service Short Code"
                                            field={field}
                                            labelColor="#FFFFFF"
                                            disableLabel
                                        />
                                    )}
                                />
                                <span className={"error"}>
                                    {errors.serviceCode?.message}

                                </span>

                            </InputBox>
                        </div>
                    )}


                <InputBox>
                    <Controller
                        name="accountNumber"
                        control={control}
                        rules={{
                            required: "Please enter the beneficiary's account number"
                        }}
                        render={({ field: { onChange, value, ...field } }) => (
                            <Input
                                type="number"
                                label={"Account/Momo number"}
                                labelColor="#FFFFFF"
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    onChange(e.target.value);
                                }}
                                value={value}
                                {...field}
                                disableLabel
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.accountNumber?.message}

                    </span>

                </InputBox>

                <InputBox>
                    <Controller
                        name="accountName"
                        control={control}
                        rules={{
                            required: "Please enter the beneficiary's account name",
                        }}
                        render={({ field }) => (
                            <Input
                                type="text"
                                label="Account name"
                                labelColor="#FFFFFF"
                                field={field}
                                disableLabel
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.accountName?.message}
                    </span>

                </InputBox>


                <InputBox>
                    <Controller
                        name="accountType"
                        control={control}
                        rules={{
                            required: "The account type is required",
                        }}
                        render={({ field }) => (
                            <CustomSelect
                                placeholder="Account Type"
                                $width="100%"
                                field={field}
                                disableLabel
                                options={accountTypes.slice(1)}
                                onChange={field.onChange}
                                lightBorder={true}
                            />
                        )}
                    />
                    <span className={"error"}>
                        {errors.accountType?.message}

                    </span>
                </InputBox>

                <InputBox>
                    <Controller
                        name="description"
                        control={control}
                        rules={{
                            required: false,
                        }}
                        render={({ field }) => (
                            <Input
                                type="text"
                                label="Payment description (optional)"
                                field={field}
                                labelColor="#FFFFFF"
                                disableLabel
                            />
                        )}
                    />
                </InputBox>

                <ContinueButton>
                    <span className="form-label">

                        Funds should arrive within 1-3 working days.
                    </span>

                    <ItemWrapper>
                        <Button
                            onClick={handlePrevious}
                            label="Prev"
                            height="48px"
                            theme="alternate"
                            width="203px"
                        />

                        <Button
                            onClick={handleSubmit(onSubmit)}
                            label="Continue"
                            height="48px"
                        />
                    </ItemWrapper>
                </ContinueButton>
            </Form>
        </ContentWrapper>
    )
}

export default Beneficiary